import { useEffect, useMemo } from 'preact/hooks';
import type { Signal } from '@preact/signals';
import { getOrCreateEntry, queryRunner, scheduleDropIfUnused } from '@/stores/queryCache';

export interface UseQueryOptions {
  /** ms before considered stale (default 45_000) */
  staleTime?: number;
  /** when false, skip the fetch */
  enabled?: boolean;
  /** when set, re-fetches on this interval (ms) while the hook is mounted */
  refetchInterval?: number;
}

export interface QueryResult<T> {
  data: Signal<T | undefined>;
  loading: Signal<boolean>;
  error: Signal<Error | null>;
  refetch: () => Promise<T>;
}

const DEFAULT_STALE_TIME = 45_000;

export function useQuery<T>(
  key: string,
  fetcher: () => Promise<T>,
  opts: UseQueryOptions = {},
): QueryResult<T> {
  const { staleTime = DEFAULT_STALE_TIME, enabled = true, refetchInterval } = opts;
  const entry = useMemo(() => {
    // Pin the entry against `dropEntryIfUnused` between this render and the
    // useEffect commit by bumping refCount synchronously. Sin esto, cuando
    // el key cambia, el cleanup del effect previo corre primero y borra la
    // entrada recién creada (refCount=0) antes de que el nuevo effect la
    // pinee. La siguiente runFetch crea otra entry mientras la página sigue
    // suscrita a la huérfana — UI queda en blanco aunque la respuesta llegue.
    const e = getOrCreateEntry<T>(key);
    e.refCount += 1;
    return e;
  }, [key]);

  // Always update fetcher pointer so invalidate() uses latest closure.
  entry.fetcher = fetcher;

  // El refCount sube una vez por key (useMemo) y baja una vez por key. Antes
  // bajaba en el cleanup del efecto de fetch, que también depende de
  // `enabled`: una query que pasaba de enabled a disabled quedaba con
  // refCount 0 estando montada y se trataba como abandonada.
  useEffect(
    () => () => {
      entry.refCount = Math.max(0, entry.refCount - 1);
      // Se conserva unos minutos por si se vuelve a la página (GC_TIME_MS).
      scheduleDropIfUnused(key);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );

  useEffect(() => {
    if (!enabled) return;
    const stale = Date.now() - entry.updatedAt > staleTime;
    if (stale && !entry.inflight) {
      void queryRunner.runFetch(key, fetcher).catch(() => {
        // error already captured in entry.error
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  // Polling: refetch on a fixed interval while the hook is mounted.
  useEffect(() => {
    if (!enabled || !refetchInterval || refetchInterval <= 0) return;
    const timer = setInterval(() => {
      void queryRunner.runFetch(key, entry.fetcher!).catch(() => {
        // error captured in entry.error
      });
    }, refetchInterval);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, refetchInterval]);

  return {
    data: entry.data,
    loading: entry.loading,
    error: entry.error,
    refetch: () => queryRunner.runFetch(key, entry.fetcher!),
  };
}
